import { CurrencySelect } from "@/components/select/CurrencySelect.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { JobProfileMoveModal } from "@/pages/settings/jobs/roles/widgets/JobProfileMoveModal.tsx";
import { useJobProfileUpdate } from "@/queries/use-job-profiles.query.ts";
import { useEmployees } from "@/queries/employees/use-employees.query.ts";
import { ApiJobProfile } from "@/types/job-profile.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Input,
  Modal,
  ModalProps,
  PaginationBlock,
  Skeleton,
  Stack,
  Text,
  Title,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";

interface JobProfileEditModalProps extends ModalProps {
  profile: ApiJobProfile;
}

export function JobProfileEditModal({ profile, ...props }: JobProfileEditModalProps) {
  const { id } = { ...props };
  const { t } = useTranslation();
  const { close, openModal } = useModals();
  const { showInfo } = useToasts();
  const navigate = useNavigate();
  const [employeesPage, setEmployeesPage] = useState(1);
  const [showAllProfileEmployees, setShowAllProfileEmployees] = useState(false);
  const {
    data: profileEmployees,
    isLoading: isLoadingProfileEmployees,
    isError: isProfileEmployeesError,
  } = useEmployees({ job_profile_ids: [profile.id], page_number: employeesPage });

  const nullableNumber = (schema: z.ZodNumber) =>
    z.preprocess(
      (value) =>
        value === "" || value === null ? null : value === undefined ? undefined : Number(value),
      schema.nullable().optional()
    );
  const profileSchema = z.object({
    title: z.string().nullable(),
    description: z.string().nullable(),
    currency_code: z.string().nullable(),
    salary_min: nullableNumber(
      z.number().min(0, t("api_errors:minimum_value_must", { minimum: 0 }))
    ),
    salary_max: nullableNumber(
      z.number().min(0, t("api_errors:minimum_value_must", { minimum: 0 }))
    ),
    standard_weekly_hours: nullableNumber(
      z
        .number()
        .min(0, t("api_errors:minimum_value_must", { minimum: 0 }))
        .max(168, t("api_errors:maximum_value_must", { maximum: 168 }))
    ),
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(profileSchema),
    defaultValues: {
      title: profile.display_title ?? profile.system_title,
      description: profile.description ?? "",
      currency_code: profile.salary?.currency?.code ?? null,
      salary_min:
        profile.salary?.salary_min != null ? Number(profile.salary.salary_min) : undefined,
      salary_max:
        profile.salary?.salary_max != null ? Number(profile.salary.salary_max) : undefined,
      standard_weekly_hours: profile.standard_weekly_hours ?? undefined,
    },
  });

  const title = watch("title");
  const profileUpdate = useJobProfileUpdate(control);

  const openMoveModal = (target: "role" | "level") => {
    openModal(JobProfileMoveModal, {
      profile,
      target,
      onMoved: (movedProfile) => {
        close(id);
        showInfo({
          title:
            target === "role"
              ? t("core:job_profile_moved_to_role", { role: movedProfile.role.title })
              : t("core:job_profile_moved_to_level", { level: movedProfile.level.title }),
          icon: <FontAwesomeIcon icon="fa-light fa-check" />,
        });
        navigate(`/settings/jobs/roles/${movedProfile.role.id}`);
      },
    });
  };

  const onSubmit = handleSubmit(async (data) => {
    await profileUpdate.mutateAsync({
      id: profile.id,
      payload: {
        job_role_id: profile.role.id,
        job_level_id: profile.level.id,
        title: data.title || null,
        description: data.description || null,
        currency_code: data.currency_code || null,
        salary_min: data.salary_min ?? null,
        salary_max: data.salary_max ?? null,
        standard_weekly_hours: data.standard_weekly_hours ?? null,
      },
    });

    close(id);
  });

  return (
    <Modal
      {...props}
      title={t("core:job_profile")}
      caption={t("core:job_profile_for_role", { title, role: profile.role.title })}
      size="lg"
      withCloser
      headerActionsSlot={
        <DropdownMenu
          trigger={
            <Button
              variant="secondary"
              rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
            >
              {t("common:actions")}
            </Button>
          }
        >
          <DropdownMenuItem
            title={`${t("core:move_job_profile_to_another_role")}...`}
            onClick={() => openMoveModal("role")}
          />
          <DropdownMenuItem
            title={`${t("core:move_job_profile_to_another_level")}...`}
            onClick={() => openMoveModal("level")}
          />
        </DropdownMenu>
      }
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={profileUpdate.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
          <Button
            onClick={() => {
              close(id);
            }}
            variant={"plain"}
          >
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack>
          <Input
            label={t("core:display_name")}
            type="text"
            size="lg"
            {...register("title")}
            error={errors.title?.message}
          />
          {title !== profile.system_title ? (
            <Box className="py-2 text-sm text-secondary">
              {t("core:original_name_hint", { name: profile.system_title })}&nbsp;
              <Text
                className={"underline hover:no-underline cursor-pointer"}
                onClick={() => {
                  setValue("title", profile.system_title);
                }}
              >
                {t("core:reset_to_original")}
              </Text>
            </Box>
          ) : undefined}

          <Title size={"xs"} header={t("core:salary")} paddingTop paddingBottom />
          <Stack direction="horizontal" gap="sm">
            <Controller
              control={control}
              name="currency_code"
              render={({ field }) => (
                <CurrencySelect
                  label={t("common:currency")}
                  emptyTitle={t("common:not_selected")}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.currency_code?.message}
                />
              )}
            />
            <Input
              label={t("core:salary_min")}
              type="number"
              {...register("salary_min")}
              error={errors.salary_min?.message}
            />
            <Input
              label={t("core:salary_max")}
              type="number"
              {...register("salary_max")}
              error={errors.salary_max?.message}
            />
          </Stack>

          <Input
            label={t("core:standard_weekly_hours")}
            type="number"
            min={0}
            max={168}
            step={0.5}
            {...register("standard_weekly_hours")}
            error={errors.standard_weekly_hours?.message}
          />
          <Box className={"py-2 text-sm text-secondary"}>
            {`${t("core:standard_weekly_hours_hint")} ${t(
              "core:standard_weekly_hours_inherit_legal_entity"
            )}`}
          </Box>

          <Title
            size={"xs"}
            header={t("core:job_profile_description")}
            caption={t("core:job_profile_description_hint")}
            paddingTop
            paddingBottom
          />

          <Controller
            render={({ field: { value, onChange } }) => (
              <RichEditor
                value={value || ""}
                onChange={onChange}
                error={errors.description?.message}
              />
            )}
            control={control}
            name={"description"}
          />

          <Title
            size="xs"
            header={
              isLoadingProfileEmployees
                ? t("common:loading")
                : t("core:used_by", {
                    target: t("plurals:employees_count", {
                      count: profileEmployees?.metadata.total_items ?? 0,
                    }),
                  })
            }
            paddingTop
            paddingBottom
          />

          {isLoadingProfileEmployees ? (
            <Stack gap="sm">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </Stack>
          ) : isProfileEmployeesError ? (
            <Text className="text-secondary">{t("core:job_profile_employees_load_error")}</Text>
          ) : profileEmployees?.items.length ? (
            <Stack>
              {(showAllProfileEmployees
                ? profileEmployees.items
                : profileEmployees.items.slice(0, 3)
              ).map((employee) => (
                <EmployeeListItem
                  key={employee.id}
                  employee={employee}
                  interactive="modal"
                  valueSlot={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
                />
              ))}
              {!showAllProfileEmployees && profileEmployees.metadata.total_items > 3 && (
                <Stack direction="horizontal" className="pt-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowAllProfileEmployees(true)}
                  >
                    {t("common:show_more")}
                  </Button>
                </Stack>
              )}
              {showAllProfileEmployees && profileEmployees.metadata.total_pages > 1 && (
                <PaginationBlock
                  metadata={profileEmployees.metadata}
                  onPageChange={setEmployeesPage}
                />
              )}
            </Stack>
          ) : (
            <Text className="text-secondary">{t("core:job_profile_employees_empty")}</Text>
          )}
        </Stack>
      </form>
    </Modal>
  );
}
